import { createFileRoute } from "@tanstack/react-router";
import { AppHeader } from "@/components/AppHeader";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/results")({
  head: () => ({ meta: [{ title: "All Markets Chart — Brothers Club" }] }),
  component: ResultsPage,
});

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Retention: only last 12 months of results are kept.
function lastTwelveMonths() {
  const now = new Date();
  const list: { month: number; year: number; label: string }[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    list.push({
      month: d.getMonth(),
      year: d.getFullYear(),
      label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`,
    });
  }
  return list;
}

function ResultsPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [applied, setApplied] = useState({ month, year });

  const monthOptions = lastTwelveMonths();


  const { data: games } = useQuery({
    queryKey: ["chart_games"],
    queryFn: async () => {
      const { data } = await supabase
        .from("games")
        .select("id,name,color,active")
        .eq("active", true)
        .order("name");
      return data ?? [];
    },
  });

  const monthStr = `${applied.year}-${String(applied.month + 1).padStart(2, "0")}`;
  const daysInMonth = new Date(applied.year, applied.month + 1, 0).getDate();

  const { data: results } = useQuery({
    queryKey: ["chart_results", monthStr],
    queryFn: async () => {
      const { data } = await supabase
        .from("results")
        .select("game_id, result_value, result_date")
        .gte("result_date", `${monthStr}-01`)
        .lte("result_date", `${monthStr}-${String(daysInMonth).padStart(2, "0")}`);
      return data ?? [];
    },
  });

  // Map: date -> gameId -> value
  const map = new Map<string, Map<string, string>>();
  (results ?? []).forEach((r: any) => {
    if (!map.has(r.result_date)) map.set(r.result_date, new Map());
    map.get(r.result_date)!.set(r.game_id, String(r.result_value));
  });

  const dates = Array.from({ length: daysInMonth }, (_, i) => {
    const d = i + 1;
    return `${monthStr}-${String(d).padStart(2, "0")}`;
  });

  return (
    <div>
      <AppHeader />
      <div className="gradient-header px-4 pb-6 pt-2 -mt-1">
        <h2 className="text-lg font-black text-primary-foreground">ALL MARKETS CHART</h2>
      </div>
      <div className="px-4 py-4 space-y-4">
        <div className="flex flex-wrap items-end gap-2 rounded-2xl bg-card p-3 shadow-card">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] font-semibold text-muted-foreground">Month (last 12)</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={`${year}-${month}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split("-").map(Number);
                setYear(y); setMonth(m);
              }}
            >
              {monthOptions.map((o) => (
                <option key={o.label} value={`${o.year}-${o.month}`}>{o.label}</option>
              ))}
            </select>
          </div>
          <Button
            className="gradient-primary"
            onClick={() => setApplied({ month, year })}
          >
            Search
          </Button>
        </div>


        <h3 className="text-base font-bold">
          Chart for {MONTHS[applied.month]} {applied.year}
        </h3>

        <div className="overflow-x-auto rounded-2xl bg-card shadow-card">
          <table className="w-full min-w-max text-sm">
            <thead className="bg-primary text-primary-foreground">
              <tr>
                <th className="sticky left-0 z-10 bg-primary px-3 py-2 text-left text-xs uppercase">
                  Date
                </th>
                {(games ?? []).map((g: any) => (
                  <th key={g.id} className="px-3 py-2 text-center text-xs uppercase whitespace-nowrap">
                    {g.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map((date) => {
                const [, m, d] = date.split("-");
                const label = `${d}/${m}/${applied.year}`;
                const row = map.get(date);
                return (
                  <tr key={date} className="border-t">
                    <td className="sticky left-0 z-10 bg-card px-3 py-2 font-semibold whitespace-nowrap">
                      {label}
                    </td>
                    {(games ?? []).map((g: any) => {
                      const v = row?.get(g.id);
                      return (
                        <td key={g.id} className="px-3 py-2 text-center font-black">
                          {v ? (
                            <span className="text-primary">{v}</span>
                          ) : (
                            <span className="text-muted-foreground">XX</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {(games ?? []).length === 0 && (
                <tr>
                  <td className="px-3 py-6 text-center text-muted-foreground" colSpan={1}>
                    No active markets.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
